import { useTranslation } from 'react-i18next';
import { DialogTitle, useClose } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useUnattendedOcrEmail } from 'support/helpers/ocr/ocr';
import { RelationshipV2 } from 'support/types';
import { Button } from 'components/Form/Button';

type SetupOCRExtractorProps = {
  targetRelationship: RelationshipV2;
  setOpen: (open: boolean) => void;
};

export const SetupOCRExtractor = ({ setOpen, targetRelationship }: SetupOCRExtractorProps) => {
  const { t } = useTranslation();
  const close = useClose();
  const { data: currentPartner } = useCurrentPartner();
  const unattendedOcrEmail = useUnattendedOcrEmail({
    relationshipId: targetRelationship.id,
    currentPartner,
  });
  const { isCopied, copyToClipboard } = useCopyToClipboard();

  return (
    <div className="flex max-h-200 flex-col gap-4 overflow-auto p-6 sm:max-w-xl">
      <div className="flex items-start justify-between">
        <DialogTitle as="h3" className="text-lg font-medium text-gray-900">
          {t('webedi:documentExtractor.beta.info.title')}
        </DialogTitle>
        <Button
          variant="minimal"
          iconOnly
          size="extra-small"
          LeftIcon={XMarkIcon}
          onClick={close}
          analyticsId="document-extractor:close"
        />
      </div>
      <p className="text-sm text-gray-700">
        {t('webedi:documentExtractor.beta.ocrSetup.description', {
          documentType: t(`common:messageTypes.plural.${targetRelationship.message_type}`),
        })}
      </p>
      {unattendedOcrEmail ? (
        <div className="flex items-center justify-center rounded-3xl bg-gray-100 px-6 py-2">
          <span className="text-sm text-gray-500">{unattendedOcrEmail}</span>
        </div>
      ) : (
        <div className="h-10 w-full animate-pulse rounded-3xl bg-gray-200" />
      )}
      <div className="flex justify-end">
        <Button
          type="button"
          onClick={() => {
            if (unattendedOcrEmail) {
              copyToClipboard(unattendedOcrEmail);
            }
            setOpen(false);
          }}
          analyticsId="document-extractor-beta:copy-and-close"
        >
          {isCopied
            ? t('common:ocr.upload.unattendedNudge.emailCopied')
            : t('webedi:documentExtractor.beta.ocrSetup.copyAndClose')}
        </Button>
      </div>
    </div>
  );
};
